import { useEffect } from 'react';
import { Provider as RollbarProvider, ErrorBoundary } from '@rollbar/react';

import { rollbar } from 'utils/errorLogger';
import {
  CONTENTA_POST_LOGOUT,
  CONTENTA_REDIRECT,
} from 'constants/storages-values';
import { B2B_ROUTES } from 'constants/routes';

import '../../styles/globalStyles.css';

import { LanguageProvider } from '../../contexts/languageContext';
import { AuthProvider } from '../../contexts/authContext';
import { UserProvider } from '../../contexts/userContext';
import { FindExpertProvider } from 'contexts/find-expert-context';

import { AppWrapper } from '../AppWrapper';
import CookieConsent from 'components/CookieConsent';
import BookflowModalWrapper from 'components/BookflowModalWrapper';

const urlsToNotSave = [
  '/',
  '/faq',
  '/guide/register',
  '/login',
  '/login/travel-expert',
  '/meet/*',
  '/privacy-policy',
  '/redirecting',
  '/register',
  '/terms-and-conditions',
  '/travelpartners',
  '/travelpartners/login',
  '/travelpartners/join',
  '/travelpartners/terms',
  '/b2b/admin',
  '/b2b/agent',
];
const hasContentaRedirect =
  typeof sessionStorage !== 'undefined'
    ? sessionStorage.getItem(CONTENTA_REDIRECT)
    : null;

// Auth0 only accepts the site root as logout returnTo; the /logout page leaves
// the real destination (the partner login for B2B members) here.
// Only known in-app paths are honoured: the value comes from storage, so it
// must never be able to send the browser to another origin or a javascript: URI.
const POST_LOGOUT_PATHS = [B2B_ROUTES.LOGIN];
const postLogoutPath =
  typeof sessionStorage !== 'undefined'
    ? sessionStorage.getItem(CONTENTA_POST_LOGOUT)
    : null;
if (postLogoutPath) {
  sessionStorage.removeItem(CONTENTA_POST_LOGOUT);
  if (
    POST_LOGOUT_PATHS.includes(postLogoutPath) &&
    window.location.pathname !== postLogoutPath
  ) {
    window.location.replace(postLogoutPath);
  }
}

function App() {
  const localStorageLanguage =
    typeof sessionStorage !== 'undefined'
      ? sessionStorage.getItem('@contenta-language')
      : null;
  const { pathname, search } = window.location;

  const willSaveRedirect =
    (pathname.includes('/advisor-details/') ||
      !urlsToNotSave.includes(pathname)) &&
    !hasContentaRedirect &&
    typeof sessionStorage !== 'undefined';
  if (willSaveRedirect) {
    sessionStorage.setItem(CONTENTA_REDIRECT, pathname + search);
  }

  useEffect(() => {
    const shouldUnregister = !window.location.pathname.includes('/meet');
    if (shouldUnregister && 'serviceWorker' in navigator) {
      navigator.serviceWorker
        .getRegistrations()
        .then((registrations) => {
          for (let registration of registrations) {
            registration.unregister().then(() => window.location.reload());
          }
        })
        .catch((error) => {
          console.error('Failed to unregister service workers:', error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isEmbedRoute = pathname.startsWith('/embed/');
  // Calls load no trackers, and the banner would sit on the call controls.
  const isMeetingRoute = /^\/(meet|lobby)(\/|$)/.test(pathname);

  return (
    <RollbarProvider instance={rollbar}>
      <ErrorBoundary>
        <AuthProvider>
          <UserProvider>
            <LanguageProvider language={localStorageLanguage}>
              {/* Above the routes on purpose: the landing page, /home and
                  /search-travel-expert share one set of filters, so the
                  destination picked on the landing survives navigation. */}
              <FindExpertProvider>
                <AppWrapper />
              </FindExpertProvider>
              {!isEmbedRoute && !isMeetingRoute && <CookieConsent />}
              {/* <BookflowModalWrapper /> */}
            </LanguageProvider>
          </UserProvider>
        </AuthProvider>
      </ErrorBoundary>
    </RollbarProvider>
  );
}

export default App;
